import React from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";

import { TextField } from "@mui/material";

import { TWO_DECIMAL_NUMBER_REGEX, WHOLE_NUMBER_REGEX } from "@/utils/form-validators";

import { FormField } from "./FormField";

interface NumericGoalInputProps<T extends FieldValues> {
  name: FieldPath<T>;
  label: string;
  control: Control<T>;
  rules: {
    required: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validate?: Record<string, (value: any) => boolean | string>;
  };
  onChange?: () => void | Promise<void>;
  maxLength?: number;
  "data-testid"?: string;
  inputMode?: "numeric" | "decimal";
}

const clampDecimalInput = (raw: string, maxDecimals: number) => {
  // Keep digits and at most one dot
  let s = raw.replace(/[^\d.]/g, "");

  // If more than one dot, keep the first and remove the rest
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot + 1);
    const after = s.slice(firstDot + 1).replace(/\./g, "");
    s = before + after;
  }

  // Limit decimals (but allow "123." while typing)
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot);
    const after = s.slice(firstDot + 1);
    s = `${before}.${after.slice(0, maxDecimals)}`;
  }

  // Disallow leading zeroes
  if (s.length > 1 && s.startsWith("0") && !s.startsWith("0.")) {
    s = s.replace(/^0+/, "");
  }

  return s;
};

const clampIntegerInput = (raw: string) => {
  let s = raw.replace(/\D/g, "");

  // Disallow leading zeros
  if (s.length > 1 && s.startsWith("0")) {
    s = s.replace(/^0+/, "");
  }

  return s;
};

/**
 * Reusable numeric goal input field with consistent styling and validation.
 * Automatically filters non-numeric input and provides error handling.
 *
 * @example
 * <NumericGoalInput
 *   name="weightMin"
 *   label="Minimum Weight"
 *   control={control}
 *   rules={{
 *     required: "Minimum weight is required",
 *     validate: {
 *       isNumber: v => /^[0-9]+$/.test(v || ""),
 *     }
 *   }}
 *   data-testid="minimum-weight-input"
 * />
 */
export const NumericGoalInput = <T extends Record<string, unknown>>({
  name,
  label,
  control,
  rules,
  onChange,
  maxLength = 5,
  inputMode = "numeric",
  "data-testid": dataTestId,
}: NumericGoalInputProps<T>) => {
  const isDecimal = inputMode === "decimal";

  return (
    <FormField label={label} required>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            variant="outlined"
            type="text"
            inputMode={inputMode}
            size="small"
            fullWidth
            data-testid={dataTestId}
            onKeyDown={e => {
              // Disallow negatives/scientific notation
              if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
                e.preventDefault();
              }
              // Disallow decimal point for integer fields
              if (!isDecimal && e.key === ".") {
                e.preventDefault();
              }
            }}
            onChange={async e => {
              const raw = e.target.value;

              const next = isDecimal
                ? clampDecimalInput(raw, 1) // max 1 decimal at input level
                : clampIntegerInput(raw); // integers only at input level

              const clamped = maxLength ? next.slice(0, maxLength) : next;

              field.onChange(clamped);
              if (onChange) {
                await onChange();
              }
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message || " "}
            slotProps={{
              htmlInput: {
                maxLength,
                pattern: isDecimal ? TWO_DECIMAL_NUMBER_REGEX : WHOLE_NUMBER_REGEX,
              },
            }}
          />
        )}
      />
    </FormField>
  );
};
